import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, Loader2, RefreshCw, AlertTriangle, ChevronRight, CheckCircle2, Info,
} from 'lucide-react';

const CASE_ESTADOS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'ABIERTO', label: 'Abierto' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'EN_INVESTIGACION', label: 'En Investigación' },
  { value: 'EN_REVISION', label: 'En Revisión' },
  { value: 'ESCALADO', label: 'Escalado' },
  { value: 'BLOQUEO_PREVENTIVO', label: 'Bloqueo Preventivo' },
  { value: 'EN_VIGILANCIA', label: 'En Vigilancia' },
  { value: 'CERRADO', label: 'Cerrado' },
];

export default function ROSGenerarLote() {
  const navigate = useNavigate();
  const [step, setStep] = useState('seleccion'); // 'seleccion' | 'preview'
  // selección
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('all');
  const [selected, setSelected] = useState({}); // caso_id -> bool
  // preview
  const [preparing, setPreparing] = useState(false);
  const [items, setItems] = useState([]);
  const [inusualidades, setInusualidades] = useState([]);
  const [mapeoConfigurado, setMapeoConfigurado] = useState(true);
  const [rowInus, setRowInus] = useState({}); // caso_id -> inusualidad_id
  const [templates, setTemplates] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [template, setTemplate] = useState('none');
  const [tipo, setTipo] = useState('lavado_activos');
  const [generating, setGenerating] = useState(false);
  const [loteProgress, setLoteProgress] = useState(null);

  // ---- carga de casos (server-side) ----
  const loadCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const params = { search: search || undefined, limit: 50 };
      if (estadoFiltro && estadoFiltro !== 'all') params.estado = estadoFiltro;
      const res = await api.get('/cases', { params });
      const d = res.data;
      setCases(Array.isArray(d) ? d : (d.items || d.cases || []));
    } catch { /* noop */ } finally { setCasesLoading(false); }
  }, [search, estadoFiltro]);

  useEffect(() => {
    const t = setTimeout(loadCases, 350);
    return () => clearTimeout(t);
  }, [loadCases]);

  useEffect(() => {
    (async () => {
      try {
        const [tplRes, tiposRes] = await Promise.all([
          api.get('/ros-config/templates'),
          api.get('/ros/tipos'),
        ]);
        setTemplates(tplRes.data || []);
        setTipos(tiposRes.data || []);
      } catch { /* noop */ }
    })();
  }, []);

  const toggle = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const toggleAll = () => {
    if (cases.every((c) => selected[c.id])) {
      setSelected((p) => { const n = { ...p }; cases.forEach((c) => delete n[c.id]); return n; });
    } else {
      setSelected((p) => { const n = { ...p }; cases.forEach((c) => { n[c.id] = true; }); return n; });
    }
  };

  // ---- preparar (sugerir inusualidad) ----
  const preparar = async () => {
    if (selectedIds.length === 0) { toast.error('Seleccioná al menos un caso'); return; }
    setPreparing(true);
    try {
      const res = await api.post('/ros/preparar-generacion-lote', { caso_ids: selectedIds });
      const { items: its = [], inusualidades: inus = [], mapeo_configurado } = res.data || {};
      setItems(its);
      setInusualidades(inus);
      setMapeoConfigurado(!!mapeo_configurado);
      const ri = {};
      its.forEach((it) => { ri[it.caso_id] = it.inusualidad_sugerida_id || ''; });
      setRowInus(ri);
      setStep('preview');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error preparando la generación');
    } finally { setPreparing(false); }
  };

  const setInus = (cid, val) => setRowInus((p) => ({ ...p, [cid]: val }));

  // ---- generar ----
  const generar = async () => {
    const validItems = items.filter((it) => !it.error);
    if (validItems.length === 0) { toast.error('No hay casos válidos'); return; }
    setGenerating(true);
    setLoteProgress({ procesados: 0, total: validItems.length });
    try {
      const payload = {
        items: validItems.map((it) => ({ caso_id: it.caso_id, inusualidad_id: rowInus[it.caso_id] || '' })),
        template_id: template && template !== 'none' ? template : '',
        tipo_ros: tipo || 'lavado_activos',
      };
      // Job en segundo plano (evita timeout 524 de Cloudflare con varios casos)
      const res = await api.post('/ros/generar-lote', payload);
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No se pudo iniciar la generación');
      const total = res.data.total || validItems.length;
      let job = null;
      for (let i = 0; i < 600; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const jr = await api.get(`/ros/generar-bulk-job/${jobId}`, { timeout: 20000 });
          job = jr.data;
          setLoteProgress({ procesados: job.procesados || 0, total });
          if (job.estado === 'completado' || job.estado === 'error') break;
        } catch { /* reintentar */ }
      }
      const creados = (job && job.creados) || [];
      const errores = (job && job.errores) || [];
      toast.success(`${creados.length} ROS generado(s)${errores.length ? ` · ${errores.length} con error` : ''}`);
      if (errores.length) errores.slice(0, 3).forEach((e) => toast.error(`${(e.caso_id || '').slice(0, 8)}: ${e.error}`));
      if (creados.length) navigate('/m/gestion-de-casos/ros-reports');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error generando los ROS');
    } finally { setGenerating(false); setLoteProgress(null); }
  };

  const inusLabel = (id) => {
    const i = inusualidades.find((x) => x.id === id);
    return i ? `${i.codigo} · ${i.texto_corto}` : '—';
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="ros-generar-lote-page">
      <div className="flex items-center gap-3 mb-1">
        <Button variant="ghost" size="sm" className="-ml-2 text-slate-500"
          onClick={() => step === 'preview' ? setStep('seleccion') : navigate('/m/gestion-de-casos/ros-reports')}
          data-testid="lote-back-btn">
          <ArrowLeft size={15} className="mr-1" /> {step === 'preview' ? 'Volver a selección' : 'Reportes ROS'}
        </Button>
      </div>
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
        <Shield size={22} className="text-purple-600" /> Generación de ROS por Inusualidad
      </h1>
      <p className="text-slate-500 text-sm mt-0.5 mb-4">
        Elegí los casos, revisá la <strong>inusualidad más común detectada</strong> por cada uno y el texto que se usará,
        y generá todos los ROS de una vez.
      </p>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-xs mb-4">
        <Badge className={step === 'seleccion' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-500'}>1 · Seleccionar casos</Badge>
        <ChevronRight size={14} className="text-slate-300" />
        <Badge className={step === 'preview' ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-500'}>2 · Revisar y generar</Badge>
      </div>

      {step === 'seleccion' ? (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex gap-2">
            <Input placeholder="Buscar en TODOS los casos (título, CUIT, CVU, cliente)..."
              value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1" data-testid="lote-search" />
            <Select value={estadoFiltro} onValueChange={setEstadoFiltro}>
              <SelectTrigger className="w-48" data-testid="lote-estado-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CASE_ESTADOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 cursor-pointer text-slate-600">
              <input type="checkbox" checked={cases.length > 0 && cases.every((c) => selected[c.id])}
                onChange={toggleAll} data-testid="lote-select-all" />
              Seleccionar todos los visibles
            </label>
            <span className="text-purple-700 font-medium">{selectedIds.length} seleccionado(s)</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
            {casesLoading && <p className="text-center text-slate-400 text-sm py-6"><RefreshCw size={14} className="inline animate-spin mr-1" /> Buscando...</p>}
            {!casesLoading && cases.map((c) => (
              <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer"
                data-testid={`lote-case-${c.id}`}>
                <input type="checkbox" checked={!!selected[c.id]} onChange={() => toggle(c.id)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{c.titulo}</p>
                  <p className="text-xs text-slate-400">{c.nombre_padron || c.cuit || 'Sin CUIT'} · {c.estado} · {(c.alertas || []).length || c.total_alertas || 0} alerta(s)</p>
                </div>
              </label>
            ))}
            {!casesLoading && cases.length === 0 && <p className="text-center text-slate-400 text-sm py-6">No se encontraron casos</p>}
          </div>
          <div className="p-4 border-t border-slate-200 flex justify-end">
            <Button onClick={preparar} disabled={preparing || selectedIds.length === 0}
              className="bg-purple-600 hover:bg-purple-700" data-testid="lote-preparar-btn">
              {preparing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ChevronRight size={16} className="mr-2" />}
              Revisar inusualidades ({selectedIds.length})
            </Button>
          </div>
        </Card>
      ) : (
        <>
          {!mapeoConfigurado && (
            <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
              <Info size={15} className="mt-0.5 shrink-0" />
              No hay mapeo de inusualidades ↔ monitores configurado (Config ROS → Tipos de Inusualidad).
              Por eso no se detectó automáticamente; elegí la inusualidad manualmente en cada fila.
            </div>
          )}
          {/* Config global */}
          <Card className="p-4 mb-3 grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Motivo / Tipo de ROS</label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="mt-1" data-testid="lote-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tipos.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Template de textos (global)</label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger className="mt-1" data-testid="lote-template"><SelectValue placeholder="Sin template" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin template</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Lista de ROS a generar */}
          <Card className="overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 text-sm text-slate-600">
              <strong>{items.filter((i) => !i.error).length}</strong> ROS se van a generar — revisá la inusualidad de cada uno
            </div>
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
              {items.map((it) => (
                <div key={it.caso_id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2"
                  data-testid={`lote-row-${it.caso_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{it.titulo}</p>
                    <p className="text-xs text-slate-400">
                      {it.cuit || 'Sin CUIT'} · {it.n_alertas} alerta(s)
                      {it.monitores?.length ? ` · ${it.monitores.slice(0, 2).join(', ')}${it.monitores.length > 2 ? '…' : ''}` : ''}
                    </p>
                    {it.error && <span className="text-xs text-red-500">{it.error}</span>}
                  </div>
                  {!it.error && (
                    <div className="flex items-center gap-2 sm:w-[420px]">
                      {it.inusualidad_sugerida_id && rowInus[it.caso_id] === it.inusualidad_sugerida_id && (
                        <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0" title="Detectada automáticamente">
                          <CheckCircle2 size={11} className="mr-0.5" /> auto
                        </Badge>
                      )}
                      <select
                        value={rowInus[it.caso_id] || ''}
                        onChange={(e) => setInus(it.caso_id, e.target.value)}
                        className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-purple-400"
                        data-testid={`lote-inus-${it.caso_id}`}
                      >
                        <option value="">— Sin inusualidad (texto por defecto) —</option>
                        {inusualidades.map((i) => (
                          <option key={i.id} value={i.id}>{i.codigo} · {i.texto_corto}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <AlertTriangle size={13} className="text-amber-500" />
                Se crean como <strong>borrador</strong>; los enviás a la UIF desde Reportes ROS.
              </span>
              <Button onClick={generar} disabled={generating}
                className="bg-purple-600 hover:bg-purple-700" data-testid="lote-generar-btn">
                {generating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Shield size={16} className="mr-2" />}
                {generating && loteProgress
                  ? `Generando ${loteProgress.procesados}/${loteProgress.total}...`
                  : `Generar ${items.filter((i) => !i.error).length} ROS`}
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
